import { useRouter } from 'next/router';
import type {
  GetStaticPaths,
  GetStaticProps,
  GetStaticPropsContext,
  PreviewData,
} from 'next';
import { ParsedUrlQuery } from 'querystring';
import { getNavigation } from '@/lib/sanity/queries';
import dynamic from 'next/dynamic';
import {
  getProductPageData,
  ProductPageData,
} from '@/cataloguePages/productPage/getData';
import {
  getProductCategoryData,
  ProductCategoryData,
} from '@/cataloguePages/productCategory/getData';
import { getServerUrlFromContext } from '@/lib/utils/getUrls';
import { getCategoriesResult } from './categories/getCategories';

const CataloguePageWithProductListing = dynamic(
  () => import('@/cataloguePages/productCategory')
);
const ProductPage = dynamic(() => import('@/cataloguePages/productPage'));

export type CommonProps = {
  preview: boolean;
  previewData: any | null;
  slugs: string[] | null;
};

type ProductsCatchAllProps = {
  commonProps: CommonProps;
  productCategoryData: ProductCategoryData | null;
  productPageData: ProductPageData | null;
  categoryType: 'categories' | 'articleGroup';
  categories: getCategoriesResult;
};

export const ProductsCatchAll = (props: ProductsCatchAllProps) => {
  const { commonProps, productCategoryData, productPageData, categoryType } =
    props;

  if (productPageData?.publicOmniumProduct) {
    return <ProductPage {...commonProps} {...productPageData} />;
  }

  if (productCategoryData) {
    return (
      <CataloguePageWithProductListing
        {...productCategoryData}
        {...commonProps}
      />
    );
  }

  console.error('No data found for page');

  return <></>;
};

export default ProductsCatchAll;

export const createStaticProps = async (
  context: GetStaticPropsContext<ParsedUrlQuery, PreviewData>,
  categoryType: 'categories' | 'articleGroup',
  pageType?: 'plp' | 'pdp'
) => {
  if (!categoryType) {
    throw new Error('categoryType is required');
  }

  const serverUrl = getServerUrlFromContext(context, categoryType);

  const urlObject = new URL(serverUrl);
  const slugs = urlObject.pathname?.split('/').slice(2) || [];

  const { preview = false, previewData = null, params } = context;

  if (
    slugs.includes('null') ||
    slugs.includes('undefined') ||
    slugs.includes('sdk') ||
    slugs.includes('consentconfig')
  ) {
    return {
      props: {},
      revalidate: 60 * 60,
      notFound: true,
    };
  }
  const navigation = await getNavigation();

  const { ALGOLIA_INDEX_NAME, searchClient } = await import(
    '@/lib/algolia/searchClient'
  );

  const index = searchClient.initIndex(ALGOLIA_INDEX_NAME);

  const commonProps: CommonProps = {
    preview: preview || false,
    previewData: previewData || null,
    slugs: slugs,
  };

  // PDP (Product Display Page )
  // Do not show PDP in articleGroup
  const productPageData: ProductPageData | null =
    (pageType !== 'plp' &&
      categoryType === 'categories' &&
      (await getProductPageData({
        context,
        index,
        slugs,
        categoryType,
      }))) ||
    null;

  if (productPageData) {
    // console.log('############# Product page ##################');
    return {
      props: {
        productPageData,
        commonProps,
        navigation,
        categoryType,
      },
      revalidate: 60 * 60,
    };
  }

  // PLP (Product List Page )
  const productCategoryData =
    (pageType !== 'pdp' &&
      (await getProductCategoryData({
        context,
        slugs,
        categoryType,
      }))) ||
    null;

  if (productCategoryData) {
    // console.log('############# Product category ##################');
    return {
      props: {
        productCategoryData,
        commonProps,
        navigation,
        categoryType,
        // Disable ISR ofr now. Causes issues with Algolia
        serverUrl: null,
      },
      revalidate: 60 * 60,
    };
  }

  // 404
  return {
    props: {
      commonProps,
      navigation,
      categoryType,
    },
    revalidate: 60 * 60,
    notFound: true,
  };
};

// export const getStaticProps: GetStaticProps = async (context) => {
//   console.log({ context });
//   const value = await createStaticProps(context, 'articleGroup');

//   // Disabled ISR for now. Causes issues with Algolia
//   // const { props } = value;
//   // const { productCategoryData } = props;
//   // if (productCategoryData) {
//   //   // Create props for search
//   //   const searchServerState = await getServerState(
//   //     <Search
//   //       {...productCategoryData.searchProps}
//   //       serverUrl={props.serverUrl}
//   //     />,
//   //     {
//   //       renderToString,
//   //     }
//   //   );
//   //   const returnVaue = {
//   //     ...value,
//   //     props: {
//   //       ...props,
//   //       productCategoryData: {
//   //         ...productCategoryData,
//   //         searchProps: {
//   //           ...productCategoryData.searchProps,
//   //           searchServerState,
//   //         },
//   //       },
//   //     },
//   //   };
//   //   return returnVaue;
//   // }

//   return value;
// };

export const getStaticPaths: GetStaticPaths = async () => {
  const paths: any = [];
  return {
    paths,
    fallback: 'blocking',
  };
};
